import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { assetsApi, templatesApi, queryKeys, CreateTemplateRequest } from '@/lib/api';
import { useAuthStore } from '@/store/auth';

interface Asset {
  id: number;
  facilityId: string;
  filename: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  s3Key: string;
  s3Url: string;
  thumbnailUrl?: string;
  isActive: boolean;
  duration: number;
  sortOrder: number;
  width?: number;
  height?: number;
  uploadedBy?: string;
  createdAt: string;
  updatedAt: string;
  facility: {
    facilityId: string;
    name: string;
  };
}

export default function AssetsPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  // Redirect admin users to the consolidated admin dashboard
  if (user?.role === 'admin') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto text-center">
          <div className="p-8 bg-blue-50 border border-blue-200 rounded-lg">
            <h2 className="text-xl font-semibold text-blue-900 mb-4">Admin Dashboard Available</h2>
            <p className="text-blue-700 mb-6">
              As an administrator, please use the consolidated Admin Dashboard which provides 
              overlay management, schedules, YouTube, and facility settings in a unified interface.
            </p>
            <div className="space-y-2">
              <a 
                href="/admin" 
                className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Go to Admin Dashboard
              </a>
              <p className="text-sm text-blue-600">
                You'll be redirected automatically in a few seconds...
              </p>
            </div>
          </div>
        </div>
        <script dangerouslySetInnerHTML={{
          __html: 'setTimeout(() => window.location.href = "/admin", 3000);'
        }} />
      </div>
    );
  }

  // Create template mutation
  const createTemplateMutation = useMutation({
    mutationFn: (data: CreateTemplateRequest) => templatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
      setShowTemplateDialog(false);
      setTemplateName('');
      setTemplateDescription('');
      alert('Template created successfully!');
    },
    onError: (error: any) => {
      setError(error.message || 'Failed to create template');
    },
  });
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFacility] = useState<string>('');
  const [draggedItem, setDraggedItem] = useState<number | null>(null);

  // Template creation state
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');

  // Template conflict modal state
  const [templateConflictModal, setTemplateConflictModal] = useState<{
    assetName: string;
    templates: Array<{ id: number; name: string; description: string }>;
  } | null>(null);


  // Load assets on component mount
  useEffect(() => {
    loadAssets();
  }, [selectedFacility]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params: any = { limit: 100 };
      if (user?.role === 'admin' && selectedFacility) {
        params.facilityId = selectedFacility;
      }

      const response = await assetsApi.list(params);
      setAssets(response.assets);
    } catch (err: any) {
      setError(err.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const updateAsset = async (id: number, updates: { isActive?: boolean; duration?: number }) => {
    try {
      await assetsApi.update(id, updates);
      setAssets(prev => prev.map(asset => 
        asset.id === id ? { ...asset, ...updates } : asset
      ));
    } catch (err: any) {
      setError(err.message || 'Failed to update asset');
    }
  };

  const deleteAsset = async (id: number) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;

    try {
      await assetsApi.delete(id);
      setAssets(prev => prev.filter(asset => asset.id !== id));
    } catch (err: any) {
      // Enhanced error handling for template conflicts
      if (err.status === 409 && err.data?.templates) {
        const assetName = assets.find(a => a.id === id)?.originalName || 'this asset';
        setTemplateConflictModal({
          assetName,
          templates: err.data.templates,
        });
      } else {
        setError(err.message || 'Failed to delete asset');
      }
    }
  };

  const handleDragStart = (e: React.DragEvent, assetId: number) => {
    setDraggedItem(assetId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem === targetId) {
      setDraggedItem(null);
      return;
    }

    const draggedIndex = assets.findIndex(asset => asset.id === draggedItem);
    const targetIndex = assets.findIndex(asset => asset.id === targetId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;

    // Create new order
    const newAssets = [...assets];
    const [draggedAsset] = newAssets.splice(draggedIndex, 1);
    if (draggedAsset) {
      newAssets.splice(targetIndex, 0, draggedAsset);
    }

    // Update local state immediately for better UX
    setAssets(newAssets);
    setDraggedItem(null);

    try {
      // Send reorder request to backend
      const assetIds = newAssets.map(asset => asset.id);
      const reorderData: any = { assetIds };
      
      if (user?.role === 'admin' && selectedFacility) {
        reorderData.facilityId = selectedFacility;
      }
      
      await assetsApi.reorder(reorderData);
    } catch (err: any) {
      setError(err.message || 'Failed to reorder assets');
      // Reload assets to get correct order
      loadAssets();
    }
  };


  const handleCreateTemplate = async () => {
    if (!templateName.trim()) {
      setError('Template name is required');
      return;
    }

    try {
      const facilityId = user?.role === 'facility' ? user.facilityId! : selectedFacility;
      if (!facilityId) {
        setError('Please select a facility');
        return;
      }

      const activeAssets = assets.filter(asset => asset.isActive);
      if (activeAssets.length === 0) {
        setError('No active assets to save as template');
        return;
      }

      const templateData: CreateTemplateRequest = {
        name: templateName.trim(),
        description: templateDescription.trim(),
        facilityId,
        assets: activeAssets.map(asset => ({ assetId: asset.id, duration: asset.duration || 8, sortOrder: 0 })),
      };

      createTemplateMutation.mutate(templateData);
    } catch (err: any) {
      setError(err.message || 'Failed to create template');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))  } ${  sizes[i]}`;
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading assets...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Asset Management</h1>
          <p className="text-muted-foreground">
            Manage your facility's Singular.live Slideshow Widget assets
          </p>
        </div>
        
        <div className="flex gap-4">
          {assets.filter(asset => asset.isActive).length > 0 && (
            <Button onClick={() => setShowTemplateDialog(true)} variant="outline">
              Save as Template
            </Button>
          )}
          
          <Button onClick={loadAssets}>
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      {/* Assets Grid */}
      <div className="grid gap-6">
        {assets.length === 0 ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">No assets found</h3>
                <p className="text-muted-foreground">
                  Upload your first asset to get started with the slideshow widget.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          assets.map((asset, index) => (
            <Card
              key={asset.id}
              draggable
              onDragStart={(e) => handleDragStart(e, asset.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, asset.id)}
              className={`cursor-move transition-all ${
                draggedItem === asset.id ? 'opacity-50' : ''
              } hover:shadow-md`}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{asset.originalName}</CardTitle>
                    <CardDescription>
                      {formatFileSize(asset.fileSize)} • 
                      {asset.width && asset.height && ` ${asset.width}×${asset.height}`}
                    </CardDescription>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge variant={asset.isActive ? 'default' : 'secondary'}>
                      {asset.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      #{index + 1}
                    </span>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="flex gap-6">
                  {/* Asset Preview */}
                  <div className="flex-shrink-0">
                    <img
                      src={asset.thumbnailUrl || asset.s3Url}
                      alt={asset.originalName}
                      className="w-32 h-24 object-cover rounded-md border"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder-image.png';
                      }}
                    />
                  </div>
                  
                  {/* Asset Controls */}
                  <div className="flex-1 space-y-4">
                    {/* Active/Inactive Toggle */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={asset.isActive}
                          onChange={(e) => updateAsset(asset.id, { isActive: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm font-medium">Active in slideshow</span>
                      </label>
                    </div>
                    
                    {/* Duration Control */}
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium min-w-[80px]">Duration:</label>
                      <input
                        type="number"
                        min="1"
                        max="300"
                        value={asset.duration}
                        onChange={(e) => {
                          const duration = parseInt(e.target.value);
                          if (duration >= 1 && duration <= 300) {
                            updateAsset(asset.id, { duration });
                          }
                        }}
                        className="w-20 px-2 py-1 border border-input rounded text-sm bg-background text-foreground"
                      />
                      <span className="text-sm text-muted-foreground">
                        seconds ({formatDuration(asset.duration)})
                      </span>
                    </div>
                    
                    {/* Asset Info */}
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div>File: {asset.filename}</div>
                      <div>Type: {asset.mimeType}</div>
                      <div>Uploaded: {new Date(asset.createdAt).toLocaleDateString()}</div>
                      {asset.uploadedBy && <div>By: {asset.uploadedBy}</div>}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(asset.s3Url, '_blank')}
                    >
                      View Full
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteAsset(asset.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>


      {/* Template Creation Dialog */}
      {showTemplateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Save as Template</h2>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowTemplateDialog(false);
                    setTemplateName('');
                    setTemplateDescription('');
                    setError('');
                  }}
                >
                  ×
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Save your current active asset configuration as a reusable template.
              </p>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="templateName" className="block text-sm font-medium mb-2">
                    Template Name *
                  </label>
                  <input
                    id="templateName"
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    placeholder="Enter template name"
                    required
                    disabled={createTemplateMutation.isPending}
                  />
                </div>
                
                <div>
                  <label htmlFor="templateDescription" className="block text-sm font-medium mb-2">
                    Description (optional)
                  </label>
                  <textarea
                    id="templateDescription"
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    rows={3}
                    placeholder="Describe this template configuration"
                    disabled={createTemplateMutation.isPending}
                  />
                </div>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    This template will include {assets.filter(asset => asset.isActive).length} active assets 
                    with their current duration settings and display order.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowTemplateDialog(false);
                    setTemplateName('');
                    setTemplateDescription('');
                    setError('');
                  }}
                  disabled={createTemplateMutation.isPending}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateTemplate}
                  disabled={createTemplateMutation.isPending || !templateName.trim()}
                >
                  {createTemplateMutation.isPending ? 'Creating...' : 'Create Template'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Conflict Modal */}
      {templateConflictModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-red-900 dark:text-red-100">Cannot Delete Asset</h2>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    {templateConflictModal.assetName} is used in {templateConflictModal.templates.length} template(s)
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                To delete this asset, you must first delete or modify these templates:
              </p>

              <div className="space-y-3 max-h-64 overflow-y-auto">
                {templateConflictModal.templates.map((template) => (
                  <div key={template.id} className="p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{template.name}</p>
                        {template.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{template.description}</p>
                        )}
                      </div>
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">ID: {template.id}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  💡 <strong>Tip:</strong> Go to the Templates tab to manage these templates. You can either delete them or edit them to remove this asset.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setTemplateConflictModal(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drag and Drop Instructions */}
      {assets.length > 1 && (
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <h3 className="font-medium text-blue-900 mb-2">💡 Drag and Drop Ordering</h3>
          <p className="text-sm text-blue-700">
            Drag and drop assets to reorder them in the slideshow. The order shown here will be
            the order they appear in the Singular.live widget.
          </p>
        </div>
      )}
    </div>
  );
}
